<template>
  <section v-if="hasShown" ref="root" class="cms-sheet" :style="sheetStyle">
    <header class="cms-sheet__header">
      <c-button-icon
        v-if="showBack"
        flat
        color="grey-800"
        icon="fas fa-arrow-left"
        @click="stack.back()"
      />

      <h4 class="cms-sheet__title">
        <slot name="title">{{ title }}</slot>
      </h4>

      <c-button-icon
        flat
        color="grey-800"
        icon="fas fa-times"
        @click="stack.close()"
      />
    </header>

    <div class="cms-sheet__body">
      <slot :push="stack.push" :back="stack.back" :close="stack.close" />
    </div>

    <footer v-if="$slots.footer" class="cms-sheet__footer">
      <slot
        name="footer"
        :push="stack.push"
        :back="stack.back"
        :close="stack.close"
      />
    </footer>
  </section>
</template>

<script setup lang="ts">
import type { CSSProperties } from "vue";
import { computed, onBeforeUnmount, onMounted, ref, watchEffect } from "vue";
import type { ModalSize } from "shared/constants/modal.constants";
import { useModalStack } from "./useModalStack";

const props = withDefaults(
  defineProps<{
    name: string;
    title?: string;
    // One of MODAL_SIZES; the stack morphs the card width to the active sheet.
    size?: ModalSize;
    showBack?: boolean;
  }>(),
  {
    title: "",
    size: "md",
    showBack: false,
  },
);

const stack = useModalStack();
const root = ref<HTMLElement | null>(null);

onMounted(() => stack.registerSheet(props.name, { root, size: props.size }));
onBeforeUnmount(() => stack.unregisterSheet(props.name));

const isActive = computed(() => stack.activeName.value === props.name);
const isOutgoing = computed(() => stack.outgoing.value === props.name);
const onStage = computed(() => isActive.value || isOutgoing.value);

// Render once first shown, then keep the sheet mounted (hidden when off-stage)
// so its state — e.g. an open "Meer opties" — survives navigating away and back.
const hasShown = ref(false);
watchEffect(() => {
  if (onStage.value) {
    hasShown.value = true;
  }
});

const sheetStyle = computed<CSSProperties>(() => {
  const transitioning = stack.outgoing.value !== null;
  const forward = stack.direction.value === "forward";
  const animating = stack.animating.value;

  let transform = "translateX(0)";

  if (transitioning) {
    if (isActive.value) {
      // Incoming: starts off-screen on the side it enters from, ends centred.
      transform = animating
        ? "translateX(0)"
        : forward
          ? "translateX(100%)"
          : "translateX(-100%)";
    } else if (isOutgoing.value) {
      // Outgoing: starts centred, exits to the opposite side.
      transform = animating
        ? forward
          ? "translateX(-100%)"
          : "translateX(100%)"
        : "translateX(0)";
    }
  }

  return {
    // Hidden when off-stage, but kept in the DOM to preserve its state.
    display: onStage.value ? undefined : "none",
    // The active sheet stays in flow so the viewport auto-fits it at rest;
    // the outgoing one is taken out of flow so it doesn't affect the height.
    position: isOutgoing.value ? "absolute" : "relative",
    inset: isOutgoing.value ? "0 0 auto 0" : "auto",
    transform,
    transition: animating ? "transform 300ms ease-in-out" : "none",
  };
});
</script>

<style lang="scss" scoped>
// Cap the sheet at the modal max height so a tall sheet scrolls its body
// instead of being clipped (header/footer stay put, so the actions remain
// reachable). Short sheets still size to their content.
.cms-sheet {
  @apply tw-flex tw-max-h-[90vh] tw-w-full tw-flex-col;
}

.cms-sheet__header {
  @apply tw-flex tw-shrink-0 tw-items-center tw-gap-4 tw-border-b tw-px-6 tw-py-5;
}

.cms-sheet__title {
  @apply tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-lg tw-font-semibold;
}

// Only the body scrolls, so the sheet's header/footer stay put.
.cms-sheet__body {
  @apply tw-min-h-0 tw-flex-auto tw-space-y-6 tw-overflow-y-auto tw-p-6;
}

.cms-sheet__footer {
  @apply tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-border-t tw-px-6 tw-py-4;
}
</style>
